import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Upload, FileText, Check } from 'lucide-react';
import { PAYMENT_MODE, options, today } from '../../constants/garmentCodes';
import { uploadFileToMinio } from '../../customHooks/S3Upload';

const inputCls = 'w-full mt-1.5 px-3 py-2.5 text-sm border border-[#E5E3DE] rounded-lg outline-none focus:border-[#3D52A0]';
const RECEIPT_SUBMITTED = 2;
const newReceiptNumber = () => `PR-${today().replace(/-/g, '')}-${String(Date.now()).slice(-4)}`;
export const STEP_LABELS = ['Select Engagement', 'Payment Details', 'Upload Evidence', 'Review & Submit'];

export default function PaymentReceiptForm({ engagements = [], defaultEngagementId = '', userId, onSubmit, onCancel, onStepChange }) {
  const [step, setStep] = useState(1);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    if (onStepChange) onStepChange(step);
  }, [step, onStepChange]);

  const [form, setForm] = useState({
    engagementId: defaultEngagementId || (engagements[0]?.id ? String(engagements[0].id) : ''),
    amount: '',
    paymentDate: today(),
    mode: '',
    transactionId: '',
    utr: '',
    notes: '',
    attachments: 0,
    evidenceUrl: '',
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const selectedEngagement = engagements.find((e) => String(e.id) === String(form.engagementId));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFileToMinio(file, 'receipts');
      if (url) {
        setUploadedFiles((prev) => [...prev, { name: file.name, url, size: file.size }]);
        setForm((f) => ({
          ...f,
          attachments: (f.attachments || 0) + 1,
          evidenceUrl: url,
        }));
      }
    } catch (err) {
      console.error('Receipt upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    onSubmit({
      engagementId: Number(form.engagementId),
      receiptNumber: newReceiptNumber(),
      amount: Number(form.amount),
      paymentDate: form.paymentDate,
      submittedDate: today(),
      mode: form.mode ? Number(form.mode) : null,
      transactionId: form.transactionId || null,
      utr: form.utr || null,
      attachments: form.attachments || (uploadedFiles.length > 0 ? uploadedFiles.length : 0),
      submittedByUserId: userId,
      status: RECEIPT_SUBMITTED,
    });
    setStep(1);
  };

  return (
    <div className="flex flex-col gap-6">

      {/* Step 1 — Select Engagement */}
      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#5C5A54]">Manufacturer</label>
            <input
              className={inputCls}
              value={selectedEngagement?.manufacturerName || ''}
              readOnly
              placeholder="Select an engagement first"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C5A54]">Engagement</label>
            <select className={inputCls} value={form.engagementId} onChange={set('engagementId')}>
              <option value="">Select engagement</option>
              {engagements.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          {form.engagementId && (
            <div className="bg-[#F4F3F0] rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#9B9893]">Current balance</span>
                <span className="font-semibold text-[#1A1915]">
                  {selectedEngagement?.walletBalance != null ? `₹${Number(selectedEngagement.walletBalance).toLocaleString('en-IN')}` : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#9B9893]">Available balance</span>
                <span className="font-semibold text-[#1A6B3E]">
                  {selectedEngagement?.availableBalance != null ? `₹${Number(selectedEngagement.availableBalance).toLocaleString('en-IN')}` : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#9B9893]">Pending receipts</span>
                <span className="font-semibold text-[#92560C]">
                  {selectedEngagement?.pendingReceipts ?? 0} under review
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 2 — Payment Details */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-[#5C5A54]">Amount (₹) *</label>
              <input type="number" placeholder="0.00" min="1" required value={form.amount} onChange={set('amount')} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C5A54]">Payment date *</label>
              <input type="date" required value={form.paymentDate} onChange={set('paymentDate')} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C5A54]">Payment mode *</label>
            <select required value={form.mode} onChange={set('mode')} className={inputCls}>
              <option value="">Select mode</option>
              {options(PAYMENT_MODE).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-[#5C5A54]">Transaction ID *</label>
              <input type="text" placeholder="NEFT/UPI reference" value={form.transactionId} onChange={set('transactionId')} className={`${inputCls} font-mono`} />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5C5A54]">UTR number</label>
              <input type="text" placeholder="UTR if available" value={form.utr} onChange={set('utr')} className={`${inputCls} font-mono`} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C5A54]">Notes</label>
            <textarea rows={2} placeholder="Any additional context..." value={form.notes} onChange={set('notes')} className={`${inputCls} resize-none`} />
          </div>
        </div>
      )}

      {/* Step 3 — Upload Evidence */}
      {step === 3 && (
        <div className="space-y-4">
          <p className="text-sm text-[#5C5A54]">Upload your payment screenshot, bank receipt, or confirmation PDF.</p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,application/pdf"
            onChange={handleFileUpload}
            className="hidden"
          />

          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            className="border-2 border-dashed border-[#C9C7C2] rounded-xl p-8 text-center hover:border-[#3D52A0] hover:bg-[#EEF0FB] transition-colors cursor-pointer"
          >
            <Upload size={28} className="text-[#9B9893] mx-auto mb-3" />
            <p className="text-sm font-medium text-[#5C5A54]">
              {uploading ? 'Uploading to Minio...' : 'Drop files here or click to upload'}
            </p>
            <p className="text-xs text-[#9B9893] mt-1">PNG, JPG, PDF up to 10 MB</p>
          </div>

          {uploadedFiles.length > 0 && (
            <div className="space-y-2">
              {uploadedFiles.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg text-xs">
                  <div className="flex items-center gap-2 text-green-800 font-medium">
                    <FileText size={14} />
                    <span>{f.name}</span>
                  </div>
                  <span className="flex items-center gap-1 text-green-700 font-semibold">
                    <Check size={13} /> Uploaded to Minio
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="text-xs text-[#9B9893] space-y-1">
            <p>• Transaction screenshot is required for online payments</p>
            <p>• Bank receipt or confirmation PDF also accepted</p>
            <p>• File size limit: 10 MB per file</p>
          </div>
        </div>
      )}

      {/* Step 4 — Review & Submit */}
      {step === 4 && (
        <div className="space-y-4">
          <div className="bg-[#E8F5EE] border border-[#A8DEC0] rounded-xl p-4 flex items-start gap-3">
            <CheckCircle size={16} className="text-[#1A6B3E] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#1A6B3E]">Ready to submit</p>
              <p className="text-xs text-[#1A6B3E]/80 mt-0.5">Your wallet will update after the manufacturer verifies and approves this payment.</p>
            </div>
          </div>
          <div className="bg-[#FAFAF8] rounded-xl border border-[#E5E3DE] divide-y divide-[#F4F3F0]">
            {[
              ['Manufacturer', selectedEngagement?.manufacturerName || '—'],
              ['Engagement', selectedEngagement?.name || '—'],
              ['Amount', form.amount ? `₹${Number(form.amount).toLocaleString('en-IN')}` : '—'],
              ['Payment mode', options(PAYMENT_MODE).find((o) => String(o.value) === String(form.mode))?.label || '—'],
              ['Transaction ID', form.transactionId || '—'],
              ['UTR', form.utr || '—'],
              ['Evidence Attached', uploadedFiles.length > 0 ? `${uploadedFiles.length} file(s) on MinIO` : 'None'],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between px-4 py-3 text-sm">
                <span className="text-[#9B9893]">{k}</span>
                <span className="font-medium text-[#1A1915]">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer — step dots + navigation */}
      <div className="flex items-center justify-between pt-2 border-t border-[#E5E3DE]">
        <div className="flex gap-1.5">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all ${
                s === step ? 'w-8 bg-[#3D52A0]' : s < step ? 'w-3 bg-[#A8DEC0]' : 'w-3 bg-[#E5E3DE]'
              }`}
            />
          ))}
        </div>
        <div className="flex gap-3">
          {step > 1 && (
            <button type="button" onClick={() => setStep((s) => s - 1)} className="px-4 py-2 text-sm text-[#5C5A54] border border-[#E5E3DE] rounded-lg hover:bg-[#F4F3F0]">
              Back
            </button>
          )}
          {step < 4 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={(step === 1 && !form.engagementId) || uploading}
              className="px-4 py-2 text-sm font-medium text-white bg-[#3D52A0] rounded-lg hover:bg-[#2D3F80] disabled:opacity-50"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#1A6B3E] rounded-lg hover:bg-[#155730] disabled:opacity-50"
            >
              <CheckCircle size={14} />
              Submit receipt
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
